import auth from "../auth/authController.js";
import userController from "../user/userController.js";
import { createLogger } from '@utils/logger.js';

class AppNav {
  constructor() {
    if (AppNav.instance) return AppNav.instance;
    AppNav.instance = this;

    this.logger = createLogger('AppNav');
    this.auth = auth;
    this.userController = userController;
    this.searchBlurTimeout = null; // For seamless transitions

    this.setupEventListener();
  }

  setupEventListener() {
    document.addEventListener("click", async (e) => {
      const target = e.target;
      const targetId = target.id;
      const targetClass = target.classList;
      const side_panel = target.closest(".app-nav-side-panel");
      const new_button = target.closest("#app-nav-new");
      const new_dropdown_button = target.closest(
        ".app-nav-dropdown-new-button"
      );
      const app_nav = document.getElementById("app-nav");
      const person = target.closest("#person");
      const overlay = document.getElementById("overlay");

      if (!app_nav) {
        return;
      }

      if (
        (!side_panel || targetClass.contains("app-nav-side-panel-close")) &&
        overlay == null
      ) {
        this.closeSidePanels();
      }

      if (new_button) {
        this.newButtonOpen();
      }

      if (!new_button) {
        this.newButtonClose();
      }

      if (person) {
        await this.handlePersonButton();
      }

      // Handle new dropdown buttons with unified approach
      if (new_dropdown_button) {
        await this.handleClickNew(target);
        return;
      }

      switch (targetId) {
        case "app-nav-burger":
          await this.handleBurgerMenu();
          break;

        case "app-nav-search-button":
          await this.handleSearchButton();
          break;

        case "logout":
          await this.auth.logout();
          break;

        default:
          break;
      }
    });

    // Enhanced focusout handling for all search fields
    document.addEventListener(
      "focusout",
      async (e) => {
        const target = e.target;
        const relatedTarget = e.relatedTarget;

        // Check if the target is any search field using the common class
        if (target.classList.contains("app-nav-search-field")) {
          // Clear any existing timeout first
          if (this.searchBlurTimeout) {
            clearTimeout(this.searchBlurTimeout);
            this.searchBlurTimeout = null;
          }

          // Check if focus is moving to another search-related element
          const isMovingToSearchButton =
            relatedTarget &&
            (relatedTarget.id === "app-nav-search-button" ||
              relatedTarget.id === "side-panel-search-dog-button" ||
              relatedTarget.id === "side-panel-search-activities-button");

          const isMovingToSearchField =
            relatedTarget &&
            relatedTarget.classList.contains("app-nav-search-field");

          const isMovingToSearchContainer =
            relatedTarget &&
            (relatedTarget.closest("#app-nav-search") ||
              relatedTarget.closest(".app-nav-side-panel-search"));

          // For side panel searches, check if focus remains within the same side panel
          const isMovingWithinSidePanel =
            relatedTarget &&
            target.closest(".app-nav-side-panel") &&
            relatedTarget.closest(".app-nav-side-panel") ===
              target.closest(".app-nav-side-panel");

          const shouldKeepOpen =
            isMovingToSearchButton ||
            isMovingToSearchField ||
            isMovingToSearchContainer ||
            isMovingWithinSidePanel;

          if (!shouldKeepOpen) {
            // Use delay for seamless transitions
            this.searchBlurTimeout = setTimeout(async () => {
              await this.handleSearchBlur(target);
              this.searchBlurTimeout = null;
            }, 200); // Increased delay slightly
          } else if (isMovingToSearchField && relatedTarget !== target) {
            // Direct transition between search fields - close the current one immediately
            await this.handleSearchBlur(target);
          }
        }
      },
      true
    );

    // Cancel blur timeout on focus (for seamless transitions)
    document.addEventListener(
      "focusin",
      async (e) => {
        if (e.target.classList.contains("app-nav-search-field")) {
          // Clear any pending blur timeout when focusing on a search field
          if (this.searchBlurTimeout) {
            clearTimeout(this.searchBlurTimeout);
            this.searchBlurTimeout = null;
          }
        }
      },
      true
    );

    // Handle search field input events for all search fields
    document.addEventListener("input", async (e) => {
      if (e.target.classList.contains("app-nav-search-field")) {
        await this.handleSearchInput(e);
      }
    });

    // Handle Enter key for all search fields
    document.addEventListener("keydown", async (e) => {
      if (
        e.target.classList.contains("app-nav-search-field") &&
        e.key === "Enter"
      ) {
        await this.handleSearchEnter(e);
      }
    });
  }

  /**
   * Unified handler for all "new" dropdown buttons
   * @param {HTMLElement} target - The clicked button element
   */
  async handleClickNew(target) {
    // Close the dropdown after selection
    this.newButtonClose();

    // Get the entity type from the button's ID or class
    const entityType = this.getEntityTypeFromButton(target);

    switch (entityType) {
      case "user":
        userController.handleCreateNewEntity();
        break;
      case "breed":
        // TODO: Implement breed creation when breed module is ready
        this.logger.info('Breed creation not yet implemented');
        alert('Breed management module coming soon!');
        break;
      default:
        this.logger.warn(`Unknown entity type: ${entityType}`);
        break;
    }
  }

  /**
   * Extract entity type from button element
   * @param {HTMLElement} button - The clicked button
   * @returns {string} The entity type
   */
  getEntityTypeFromButton(button) {
    // Method 1: Check for specific ID pattern (app-nav-new-*)
    if (button.id && button.id.startsWith("app-nav-new-")) {
      return button.id.replace("app-nav-new-", "");
    }

    // Method 2: Check for entity class in classList
    const entityClasses = [
      "event",
      "result",
      "dog",
      "note",
      "weight",
      "parcour",
    ];
    for (const entityClass of entityClasses) {
      if (button.classList.contains(entityClass)) {
        return entityClass;
      }
    }

    // Method 3: Check data attribute if you want to use that approach
    if (button.dataset.entity) {
      return button.dataset.entity;
    }

    // Fallback: return unknown
    return "unknown";
  }

  async closeSidePanels() {
    document.body.classList.remove("no-scroll");
    const side_panels = document.getElementsByClassName("app-nav-side-panel");
    for (const panel of side_panels) {
      panel.classList.remove("show");
    }

    // Also close any open search fields when closing side panels
    await this.closeAllSearchFields();
  }

  async closeAllSearchFields() {
    // Clear any pending timeouts
    if (this.searchBlurTimeout) {
      clearTimeout(this.searchBlurTimeout);
      this.searchBlurTimeout = null;
    }

    const searchFields = document.querySelectorAll(".app-nav-search-field");
    searchFields.forEach((field) => {
      field.classList.remove("show");
      field.value = "";
    });

    // Close search panels for side panel searches
    const searchPanels = document.querySelectorAll(
      ".app-nav-side-panel-search"
    );
    searchPanels.forEach((panel) => {
      panel.classList.remove("show");
    });
  }

  // Handler function outlines
  async handleBurgerMenu() {
    document.body.classList.add("no-scroll");
    const side_panel = document.getElementById("app-nav-side-panel-left");
    side_panel.classList.add("show");
  }

  async handleSearchButton() {
    // Close other search fields first
    await this.closeOtherSearchFields("app-nav-search-field");

    const search_field = document.getElementById("app-nav-search-field");
    if (search_field.classList.contains("show")) {
      // Field is already open, just keep it open (filtering happens on input)
      search_field.focus();
    } else {
      search_field.classList.add("show");
      search_field.focus();
    }
  }

  async closeOtherSearchFields(exceptFieldId) {
    // Clear any pending timeouts
    if (this.searchBlurTimeout) {
      clearTimeout(this.searchBlurTimeout);
      this.searchBlurTimeout = null;
    }

    const searchFields = document.querySelectorAll(".app-nav-search-field");
    searchFields.forEach((field) => {
      if (field.id !== exceptFieldId) {
        field.classList.remove("show");
        field.value = "";

        // Also close the search panel if it exists
        const searchPanel = field.closest(".app-nav-side-panel-search");
        if (searchPanel) {
          searchPanel.classList.remove("show");
        }
      }
    });
  }

  async handleSearchBlur(target) {
    target.classList.remove("show");
    target.value = "";

    // For side panel searches, also hide the search panel
    const searchPanel = target.closest(".app-nav-side-panel-search");
    if (searchPanel) {
      searchPanel.classList.remove("show");
    }
  }

  async handleSearchInput(event) {
    // TODO: Handle real-time search as user types
    this.logger.debug("Search input changed:", event.target.value);
    // Implementation: Live search suggestions or filtering
  }

  async handleSearchEnter(event) {
    // TODO: Execute search when Enter is pressed
    this.logger.debug("Search enter pressed:", event.target.value);
    // Implementation: Perform full search
  }

  async newButtonOpen() {
    const new_button = document.getElementById("app-nav-new");
    const drop_down = document.getElementById("app-nav-dropdown-new");

    if (new_button.classList.contains("show")) {
      this.newButtonClose();
    } else {
      new_button.classList.add("show");
      drop_down.classList.add("show");
    }
  }

  async newButtonClose() {
    const new_button = document.getElementById("app-nav-new");
    const drop_down = document.getElementById("app-nav-dropdown-new");
    new_button.classList.remove("show");
    drop_down.classList.remove("show");
  }

  async handlePersonButton() {
    document.body.classList.add("no-scroll");
    const side_panel = document.getElementById("app-nav-side-panel-right");
    side_panel.classList.add("show");
  }

  closeAllDropdowns() {
    // TODO: Close all open dropdowns
    const dropdowns = document.querySelectorAll(".dropdown, .admin-dropdown");
    dropdowns.forEach((dropdown) => {
      dropdown.classList.remove("active");
    });
  }

  showNotification(message, type = "info") {
    // TODO: Show user notifications
    this.logger.info(`${type.toUpperCase()}: ${message}`);
  }
}

// Create and export singleton instance
const appNav = new AppNav();
export default appNav;
